export interface QueuedMutation {
  id: string;
  path: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  createdAt: string;
}

export interface OfflineConflict {
  id: string;
  mutation: QueuedMutation;
  code: string;
  message: string;
  details?: unknown;
  detectedAt: string;
}

const DB_NAME = 'hengri-offline';
const DB_VERSION = 2;
const QUEUE = 'mutations';
const CACHE = 'cache';
const CONFLICTS = 'conflicts';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(CONFLICTS)) db.createObjectStore(CONFLICTS, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function transaction<T>(storeName: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = run(tx.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

export async function enqueueMutation(item: QueuedMutation): Promise<void> {
  await transaction(QUEUE, 'readwrite', (store) => store.put(item));
}

export async function listMutations(): Promise<QueuedMutation[]> {
  return transaction(QUEUE, 'readonly', (store) => store.getAll());
}

export async function removeMutation(id: string): Promise<void> {
  await transaction(QUEUE, 'readwrite', (store) => store.delete(id));
}

export async function saveConflict(conflict: OfflineConflict): Promise<void> {
  await transaction(CONFLICTS, 'readwrite', (store) => store.put(conflict));
}

export async function listConflicts(): Promise<OfflineConflict[]> {
  return transaction(CONFLICTS, 'readonly', (store) => store.getAll());
}

export async function removeConflict(id: string): Promise<void> {
  await transaction(CONFLICTS, 'readwrite', (store) => store.delete(id));
}

export async function retryConflict(id: string): Promise<void> {
  const conflicts = await listConflicts();
  const conflict = conflicts.find((item) => item.id === id);
  if (!conflict) return;
  const headers = { ...(conflict.mutation.headers ?? {}) };
  delete headers['If-Match'];
  await enqueueMutation({ ...conflict.mutation, id: crypto.randomUUID(), headers, createdAt: new Date().toISOString() });
  await removeConflict(id);
}

export async function cacheValue<T>(key: string, value: T): Promise<void> {
  await transaction(CACHE, 'readwrite', (store) => store.put({ key, value, savedAt: Date.now() }));
}

export async function readCachedValue<T>(key: string, maxAgeMs = 30 * 24 * 60 * 60 * 1000): Promise<T | null> {
  const row = await transaction<{ key: string; value: T; savedAt: number } | undefined>(CACHE, 'readonly', (store) => store.get(key));
  if (!row || Date.now() - row.savedAt > maxAgeMs) return null;
  return row.value;
}

export async function clearOfflineData(): Promise<void> {
  if (!('indexedDB' in globalThis)) return;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
}
